import { Stack } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { ScenarioScope } from '../scope';
import { ModelGroup, peopleIn } from './groups';
import { ModelMenu } from './ModelMenu';
import { GroupHeading } from './modelParts';
import { ModelRow } from './ModelRow';
import { modelTimeline } from './timeline';

type Props = {
  groups: ModelGroup[];
  names: Record<string, string>;
  scope: ScenarioScope;
  // The group to bring into view, when the adviser came here to see it.
  focusedGroup?: string | null;
  onViewArchived: () => void;
};

/**
 * Every model, grouped by who it covers. Each group leads with its primary model, and
 * every other model in the group is measured against it.
 */
export function ModelList({ groups, names, scope, focusedGroup, onViewArchived }: Props) {
  return (
    <Stack gap="lg">
      {groups.map(group => (
        <GroupSection
          key={group.key}
          group={group}
          names={names}
          scope={scope}
          focused={group.key === focusedGroup}
          onViewArchived={onViewArchived}
        />
      ))}
    </Stack>
  );
}

const barScale = (group: ModelGroup): number =>
  Math.max(0, ...group.models.map(model => modelTimeline(model.kpis)?.length ?? 0));

function GroupSection({
  group,
  names,
  scope,
  focused,
  onViewArchived,
}: {
  group: ModelGroup;
  names: Record<string, string>;
  scope: ScenarioScope;
  focused: boolean;
  onViewArchived: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) {
      ref.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  }, [focused]);
  const people = peopleIn(group, names);
  const count = group.models.length;

  return (
    <Stack ref={ref} gap="xs" data-testid="cashflow-model-group">
      <GroupHeading
        people={people}
        description={`${group.joint ? 'Joint' : 'Own'} · ${count} model${count === 1 ? '' : 's'}`}
      />
      {group.models.map(scenario => (
        <ModelRow
          key={scenario.id}
          scenario={scenario}
          primary={group.primary}
          scale={barScale(group)}
          scope={scope}
          menu={
            <ModelMenu
              scenario={scenario}
              scope={scope}
              group={group}
              names={names}
              onViewArchived={onViewArchived}
            />
          }
        />
      ))}
    </Stack>
  );
}
